<template>
  <v-dialog
    :value="!!account"
    max-width="720"
    scrollable
    @input="$event || close()">
    <v-card v-if="editedItem">
      <v-card-title class="d-flex justify-space-between">
        <span>{{ editedItem.name }}</span>
        <span class="cell-mono cell-muted">{{ editedItem.pk }}</span>
      </v-card-title>

      <v-card-text>
        <validation-select
          v-model="editedItem.plan"
          :items="planList"
          item-text="name"
          item-value="pk"
          label="Abonnement *"
          rules="required"
          outlined
          dense />

        <div class="edit__section">
          Modules
        </div>
        <div class="edit__switches">
          <v-switch
            v-for="module in modules"
            :key="module.key"
            v-model="editedItem[module.key]"
            :label="module.label"
            inset
            dense
            hide-details />
        </div>

        <template v-if="editedItem.account_type === 'promoteur'">
          <div class="edit__section">
            Connexions externes
          </div>
          <div
            v-for="(connection, idx) in editedItem.external_connection"
            :key="idx"
            class="edit__connection">
            <v-row dense>
              <v-col cols="4">
                <v-select
                  v-model="connection.name"
                  :items="nameList"
                  label="Nom"
                  outlined
                  dense
                  hide-details />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="connection.connection_type"
                  :items="typeList"
                  label="Type"
                  outlined
                  dense
                  hide-details />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="connection.connection_mode"
                  :items="modeList"
                  label="Mode"
                  outlined
                  dense
                  hide-details />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="connection.url"
                  label="Url"
                  outlined
                  dense
                  hide-details />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  v-model="connection.login"
                  label="Login"
                  outlined
                  dense
                  hide-details />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  v-model="connection.password"
                  label="Mot de passe"
                  outlined
                  dense
                  hide-details />
              </v-col>
              <v-col
                cols="2"
                class="d-flex align-center justify-space-between">
                <v-switch
                  v-model="connection.active"
                  title="Active"
                  inset
                  dense
                  hide-details
                  class="mt-0" />
                <v-btn
                  icon
                  small
                  color="error"
                  title="Retirer"
                  @click="editedItem.external_connection.splice(idx, 1)">
                  <v-icon small>
                    mdi-close
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
          <v-btn
            text
            small
            color="primary"
            @click="addExternalConnection">
            <v-icon
              left
              small>
              mdi-plus
            </v-icon>
            Ajouter une connexion
          </v-btn>
        </template>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-spacer />
        <v-btn
          text
          @click="close">
          Annuler
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="saving"
          @click="save">
          Enregistrer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import RESOURCES from '@batimentb/vadimm-js-api/resources'
  import { cloneDeep } from 'lodash'

  const EMPTY_CONNECTION = {
    name: null,
    url: null,
    login: null,
    password: null,
    active: false,
    connection_type: null,
    connection_mode: null
  }

  export default {
    name: 'AccountEditDialog',
    props: {
      // compte à modifier, null = fenêtre fermée
      account: {
        type: Object,
        default: null
      }
    },
    data () {
      return {
        editedItem: null,
        planList: [],
        saving: false,
        modules: [
          { key: 'electronic_signature_enabled', label: 'Signature électronique' },
          { key: 'mgc_enabled', label: 'MGC' },
          { key: 'buyer_space_enabled', label: 'Espace acquéreur' },
          { key: 'contact_sharing_enabled', label: 'Partage de contacts' },
          { key: 'brikx_enabled', label: 'Site Brikx' }
        ],
        typeList: ['api'],
        nameList: ['GRIMMO', 'SPO'],
        modeList: ['import', 'export', 'both']
      }
    },
    watch: {
      account: {
        immediate: true,
        handler (account) {
          if (!account) {
            this.editedItem = null
            return
          }
          this.editedItem = cloneDeep(account)
          this.editedItem.plan = account.plan && account.plan.pk
          if (!this.planList.length) this.fetchPlans()
        }
      }
    },
    methods: {
      async fetchPlans () {
        const { data } = await this.$api.get(RESOURCES.PLAN)
        this.planList = data.items
      },
      addExternalConnection () {
        if (!this.editedItem.external_connection) {
          this.$set(this.editedItem, 'external_connection', [])
        }
        this.editedItem.external_connection.push(cloneDeep(EMPTY_CONNECTION))
      },
      close () {
        this.$emit('close')
      },
      async save () {
        this.saving = true
        try {
          await this.$api.patch(RESOURCES.ACCOUNT, this.editedItem.pk, this.editedItem)
          this.$awn.success('Compte mis à jour')
          this.$emit('saved')
          this.close()
        } catch (e) {
          if (e.message !== 'Action annulée') this.$awn.alert('Impossible de mettre à jour le compte')
        } finally {
          this.saving = false
        }
      }
    }
  }
</script>

<style scoped>
  .edit__section {
    margin: 18px 0 8px;
    font-size: 12px;
    font-weight: 600;
    color: #71717a;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .edit__switches {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 4px 16px;
  }
  .edit__connection {
    padding: 12px;
    margin-bottom: 10px;
    border: 1px solid #ececef;
    border-radius: 8px;
  }
</style>
